import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState } from "@/command/ccUi";

const KINDS = {
  directors: { label: "Directors", fields: [["name", "Name"], ["designation", "Designation"], ["din", "DIN"], ["email", "Email"], ["status", "Status"]] },
  shareholders: { label: "Shareholders", fields: [["name", "Name"], ["shares", "Shares"], ["percentage", "%"], ["type", "Type"]] },
  board_meetings: { label: "Board Meetings", fields: [["title", "Title"], ["date", "Date"], ["agenda", "Agenda"], ["status", "Status"]] },
  resolutions: { label: "Resolutions", fields: [["number", "Resolution No."], ["title", "Title"], ["date", "Date"], ["status", "Status"]] },
};

export default function Governance() {
  const { can } = useCommand();
  const [tab, setTab] = useState("directors");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const cfg = KINDS[tab];
  const canEdit = can("governance", "CREATE") || can("governance", "MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/cc/governance/${tab}`).then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useRealtime(["governance"], load);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/cc/governance/${tab}/${editing.id}`, editing);
      else await api.post(`/cc/governance/${tab}`, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this record?")) return; await api.delete(`/cc/governance/${tab}/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Governance</h1>
      <p className="text-subtle mt-1 mb-6">Directors, shareholders, board meetings and resolutions.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(KINDS).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`gov-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-invert text-invert-ink" : "glass-medium text-subtle hover:text-ink"}`}>{v.label}</button>
        ))}
      </div>

      <Panel testid="governance-panel" action={canEdit && <CCButton onClick={() => setEditing(Object.fromEntries(cfg.fields.map(([f]) => [f, ""])))} testid="gov-add"><Plus className="w-4 h-4" /> Add</CCButton>}>
        {loading ? <LoadingState /> : rows.length === 0 ? (
          <EmptyState title={`No ${cfg.label.toLowerCase()} yet`} hint={canEdit ? "Add the first record." : undefined} />
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
              {cfg.fields.map(([f, l]) => <th key={f} className="px-3 py-3 font-medium">{l}</th>)}
              {canEdit && <th className="px-3 py-3 text-right">Actions</th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`gov-row-${r.id}`}>
                  {cfg.fields.map(([f]) => <td key={f} className="px-3 py-3">{r[f] || "—"}</td>)}
                  {canEdit && <td className="px-3 py-3 text-right">
                    <button onClick={() => setEditing({ ...r })} className="text-subtle hover:text-ink p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} className="text-subtle hover:text-red-500 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditing(null)} data-testid="gov-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">{editing.id ? "Edit" : "Add"} {cfg.label.slice(0, -1)}</h3>
            <div className="grid grid-cols-2 gap-4">
              {cfg.fields.map(([f, l]) => (
                <Field key={f} label={l}><input className={inputCls} value={editing[f] || ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`gov-field-${f}`} /></Field>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={save} testid="gov-save">Save</CCButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
